import { Button, type ButtonProps, CollapsibleTrigger, cn } from '@seen/ui'
import type { JSX } from 'react'
import { useFacesheetCard } from '#src/components/patient/detail-content/facesheet/FacesheetCardContext.ts'
import { FacesheetCollapseTriangle } from '#src/components/patient/detail-content/facesheet/FacesheetCollapseTriangle.tsx'

export type FacesheetCardCollapseTriggerProps = Omit<
  ButtonProps,
  'children' | 'variant' | 'size'
>

// Folds the card's FacesheetCardContent away, named after FacesheetCardLabel.
// Renders nothing when the card declares itself non-collapsible — the card is
// not a Collapsible root then.
//
// The whole title row is the target, not just the triangle, so this renders as
// two pieces: the triangle in the flow where the card places it, and a
// transparent button stretched over FacesheetCardTitle. An overlay rather than
// a wrapper because the row holds real links and menus on several cards
// (Conditions, Medications and Insurance link to their full pages) — nesting
// those inside a button is invalid HTML and swallows their clicks. They stay
// clickable by sitting above the overlay; FacesheetCardTitle raises them.
export function FacesheetCardCollapseTrigger({
  className,
  ...delegated
}: FacesheetCardCollapseTriggerProps): JSX.Element | null {
  const { labelId, collapsible } = useFacesheetCard()

  if (!collapsible) {
    return null
  }

  return (
    <>
      <FacesheetCollapseTriangle className="group-data-[state=closed]/facesheet-card:-rotate-90" />
      <CollapsibleTrigger asChild>
        {/* `unstyled`, not `ghost`: ghost tints its backdrop on aria-expanded,
            which an open disclosure always is, so the row would sit in a
            permanent grey box. */}
        <Button
          variant="unstyled"
          size="unstyled"
          aria-labelledby={labelId}
          data-slot="facesheet-collapse-overlay"
          className={cn(
            'absolute inset-0 rounded-sm focus-visible:ring-2 focus-visible:ring-offset-0',
            className
          )}
          {...delegated}
        />
      </CollapsibleTrigger>
    </>
  )
}
